import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { X, Factory, AlertCircle } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import apiClient from '@/api';
import { PopupField, PopupGridField, PopupCoordinates } from './PopupField';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { API_SOURCE_PATH } from './constants';
import ViewOnGoogleMaps from './ViewOnGoogleMaps';

export interface PlannedPlantPopupProps {
	longitude: number;
	latitude: number;
	properties: Record<string, string | number>;
	onClose: () => void;
}

interface PlannedPlantData {
	id: number;
	projectName: string;
	primaryFuel: string;
	projectStatus: string;
	onlineYear: number;
	onlineMonth: number;
	mwNameplate: number;
	state: string;
	county: string;
	holdingCompany: string;
	nercRegion: string;
	lastCthreeUpdate: string;
	latitude: number;
	longitude: number;
}

const fetchPlannedPlantData = async (plantId: string | number): Promise<PlannedPlantData> => {
	const response = await apiClient.get(`${API_SOURCE_PATH}/planned-plants/${plantId}`);
	return response.data;
};

export function PlannedPlantPopup({ longitude, latitude, properties, onClose }: PlannedPlantPopupProps) {
	const {
		data: fetchedData,
		isLoading,
		error
	} = useQuery({
		queryKey: ['geomap', 'planned-plant', properties.id],
		queryFn: () => fetchPlannedPlantData(properties.id),
		enabled: !!properties.id
	});

	const plannedPlantData: PlannedPlantData = {
		id: Number(properties.id) || 0,
		projectName: String(properties.projectName) || '',
		primaryFuel: String(properties.primaryFuel) || '',
		projectStatus: String(properties.projectStatus) || '',
		onlineYear: Number(properties.onlineYear) || 0,
		onlineMonth: Number(properties.onlineMonth) || 0,
		mwNameplate: Number(properties.mwNameplate) || 0,
		state: String(properties.state) || '',
		county: String(properties.county) || '',
		holdingCompany: String(properties.holdingCompany) || '',
		nercRegion: String(properties.nercRegion) || '',
		lastCthreeUpdate: String(properties.lastCthreeUpdate) || '',
		latitude: latitude,
		longitude: longitude,
		...(fetchedData || {})
	};

	const displayName = properties.name ||`Plant ${properties.id}`;

	return (
		<Card className="bg-white dark:bg-slate-900 border shadow-lg min-w-[260px] max-w-[350px]">
			<CardHeader className="pb-2">
				<div className="flex items-center justify-between">
					<CardTitle className="flex items-center gap-2 text-sm">
						<Factory className="h-4 w-4" />
						Planned Plant Details
					</CardTitle>
					<Button aria-label="close" variant="ghost" size="icon" onClick={onClose} className="h-6 w-6">
						<X className="h-3 w-3" />
					</Button>
				</div>
			</CardHeader>
			<CardContent className="space-y-3">
				<PopupCoordinates longitude={longitude} latitude={latitude} />
				<div className="grid grid-cols-2 gap-2 text-xs">
					<PopupGridField label="ID" value={properties.id.toString()} />
					<PopupField label="Name" value={displayName} />
					<PopupField label="Primary Fuel" value={properties.primaryFuel} />
					<PopupField label="Capacity" value={properties.mwNameplate ? properties.mwNameplate.toString().concat(' MW') : 'N/A'} />
				</div>
				<ViewOnGoogleMaps latitude={latitude} longitude={longitude} />
				{isLoading && (
					<div className="space-y-2">
						<Skeleton className="h-4 w-full" />
						<Skeleton className="h-4 w-3/4" />
						<Skeleton className="h-4 w-1/2" />
					</div>
				)}
				{error && (
					<div className="flex items-center gap-2 text-red-600 dark:text-red-400 text-xs">
						<AlertCircle className="h-3 w-3" />
						Failed to load additional data
					</div>
				)}
				{fetchedData && (
					<div className="space-y-2 pt-2 border-t border-slate-200 dark:border-slate-700">
						<PopupField label="Status" value={plannedPlantData.projectStatus} />
						<PopupField
							label="Online Date"
							value={
								plannedPlantData.onlineYear && plannedPlantData.onlineMonth
									? `${plannedPlantData.onlineMonth.toString().padStart(2, '0')}/${plannedPlantData.onlineYear}`
									: 'N/A'
							}
						/>
						<PopupField label="County" value={plannedPlantData.county}/>
						<PopupField label="State" value={plannedPlantData.state} />
						<PopupField label="Holding Company" value={plannedPlantData.holdingCompany} />
						<PopupField label="Nerc Region" value={plannedPlantData.nercRegion} />
						<PopupField
							label="Last update"
							value={new Date(plannedPlantData.lastCthreeUpdate).toLocaleDateString('en-US')}
						/>
					</div>
				)}
			</CardContent>
		</Card>
	);
}
